import { CSSProperties, Fragment, MouseEvent, useEffect, useMemo, useState } from 'react'

import {
  ChoiceBlockItem,
  FileBlockItem,
  HtmlBlockItem,
  SKILL_BLOCK_TYPES,
  SkillBlockItem,
  TableBlockItem,
  VIDEO_BLOCK_TYPES,
  VideoBlockItem,
  serializeSkillBlockMarkdown,
  splitChoiceBlocksFromMarkdown,
  splitFileBlocksFromMarkdown,
  splitHtmlBlocksFromMarkdown,
  splitSkillBlocksFromMarkdown,
  splitTableBlocksFromMarkdown,
  splitVideoBlocksFromMarkdown,
} from '@karakuri-web/common/markdown-to-text'
import { renderMarkdown } from '@karakuri-web/common/react-render-markdown'

import { SkillApi } from './interface'
import { renderSkill } from './Skill'
import { VideoTouchIframe } from './videoTouch/VideoTouchIframe'
import { YoutubeIframe } from './youtube/YoutubeIframe'

interface ChoiceBlockProps {
  choices: ChoiceBlockItem[]
}

interface VideoBlockProps {
  video: VideoBlockItem
}

interface SkillBlockProps {
  answerId?: string | null
  cardId?: string
  skill: SkillBlockItem
  skillApi?: SkillApi
}

interface TableBlockProps {
  table: TableBlockItem
}

interface HtmlBlockProps {
  htmlBlock: HtmlBlockItem
}

interface FileBlockProps {
  file: FileBlockItem
}

type MarkdownBlockSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'choiceBlock'
      choices: ChoiceBlockItem[]
    }
  | {
      type: 'skillBlock'
      skill: SkillBlockItem
    }
  | {
      type: 'tableBlock'
      table: TableBlockItem
    }
  | {
      type: 'htmlBlock'
      htmlBlock: HtmlBlockItem
    }
  | {
      type: 'fileBlock'
      file: FileBlockItem
    }
  | {
      type: 'videoBlock'
      video: VideoBlockItem
    }

interface MarkdownWithChoiceBlocksProps {
  answerId?: string | null
  cardId?: string
  markdown: string
  skillApi?: SkillApi
}

export function MarkdownWithChoiceBlocks({
  answerId,
  cardId,
  markdown,
  skillApi,
}: MarkdownWithChoiceBlocksProps) {
  const segments = useMemo(() => splitMarkdownBlocks(markdown), [markdown])

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'choiceBlock') {
          return <ChoiceBlock key={`choice-${index}`} choices={segment.choices} />
        }

        if (segment.type === 'videoBlock') {
          return <VideoBlock key={`video-${index}`} video={segment.video} />
        }

        if (segment.type === 'skillBlock') {
          return (
            <SkillBlock
              key={`skill-${index}`}
              answerId={answerId}
              cardId={cardId}
              skill={segment.skill}
              skillApi={skillApi}
            />
          )
        }

        if (segment.type === 'tableBlock') {
          return <TableBlock key={`table-${index}`} table={segment.table} />
        }

        if (segment.type === 'htmlBlock') {
          return <HtmlBlock key={`html-${index}`} htmlBlock={segment.htmlBlock} />
        }

        if (segment.type === 'fileBlock') {
          return <FileBlock key={`file-${index}`} file={segment.file} />
        }

        if (!segment.markdown) {
          return null
        }

        return <Fragment key={`markdown-${index}`}>{renderMarkdown(segment.markdown)}</Fragment>
      })}
    </>
  )
}

function splitMarkdownBlocks(markdown: string): MarkdownBlockSegment[] {
  const segments: MarkdownBlockSegment[] = []

  splitChoiceBlocksFromMarkdown(markdown).forEach(segment => {
    if (segment.type === 'choiceBlock') {
      segments.push(segment)
      return
    }
    splitSkillBlocksFromMarkdown(segment.markdown).forEach(skillSegment => {
      if (skillSegment.type === 'skillBlock') {
        segments.push(skillSegment)
        return
      }
      splitVideoBlocksFromMarkdown(skillSegment.markdown).forEach(videoSegment => {
        if (videoSegment.type === 'videoBlock') {
          segments.push(videoSegment)
          return
        }
        splitTableBlocksFromMarkdown(videoSegment.markdown).forEach(tableSegment => {
          if (tableSegment.type === 'tableBlock') {
            segments.push(tableSegment)
            return
          }
          splitHtmlBlocksFromMarkdown(tableSegment.markdown).forEach(htmlSegment => {
            if (htmlSegment.type === 'htmlBlock') {
              segments.push(htmlSegment)
              return
            }
            segments.push(...splitFileBlocksFromMarkdown(htmlSegment.markdown))
          })
        })
      })
    })
  })

  return segments
}

function ChoiceBlock({ choices }: ChoiceBlockProps) {
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState(0)
  const selectedChoice = choices[selectedChoiceIndex] ?? choices[0]
  const selectedChoiceKey = `${selectedChoice?.id ?? 'choice'}-${selectedChoiceIndex}`

  useEffect(() => {
    setSelectedChoiceIndex(currentIndex => Math.max(0, Math.min(currentIndex, choices.length - 1)))
  }, [choices.length])

  if (!selectedChoice) {
    return null
  }

  return (
    <div className="faq-choice-block">
      <div
        className="faq-choice-block__options"
        role="list"
        style={{
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 10,
        }}
      >
        {choices.map((choice, index) => (
          <button
            key={`${choice.id}-${index}`}
            type="button"
            className="faq-choice-block__option"
            aria-pressed={index === selectedChoiceIndex}
            onClick={() => setSelectedChoiceIndex(index)}
          >
            {choice.label}
          </button>
        ))}
      </div>
      <div key={selectedChoiceKey} className="faq-choice-block__content">
        {renderMarkdown(selectedChoice.markdown)}
      </div>
    </div>
  )
}

function VideoBlock({ video }: VideoBlockProps) {
  if (video.type === VIDEO_BLOCK_TYPES.VideoTouch && video.videoTouchId) {
    return <VideoTouchIframe videoTouchId={video.videoTouchId} />
  }

  if (video.type === VIDEO_BLOCK_TYPES.Youtube && video.youtubeId) {
    return <YoutubeIframe youtubeId={video.youtubeId} />
  }

  return null
}

function SkillBlock({ answerId, cardId, skill, skillApi }: SkillBlockProps) {
  if (!skillApi) {
    return null
  }

  if (skill.type === SKILL_BLOCK_TYPES.Form && !cardId) {
    return null
  }

  return renderSkill(skillApi, {
    from: 'FAQ_ANSWER',
    text: serializeSkillBlockMarkdown(skill),
    cardId,
    answerId,
  })
}

function TableBlock({ table }: TableBlockProps) {
  return <div className="faq-answer-table md" dangerouslySetInnerHTML={{ __html: table.html }} />
}

function HtmlBlock({ htmlBlock }: HtmlBlockProps) {
  if (!htmlBlock.html.trim()) {
    return null
  }

  return (
    <div className="faq-html-embed-block">
      <iframe
        title="HTML埋め込み"
        srcDoc={htmlBlock.html}
        sandbox=""
        referrerPolicy="no-referrer"
        style={{
          width: '100%',
          height: htmlBlock.height,
          border: 'none',
          display: 'block',
        }}
      />
    </div>
  )
}

function FileBlock({ file }: FileBlockProps) {
  if (!file.url.trim()) {
    return null
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    window.open(file.url, '_blank', 'noopener,noreferrer')
  }

  return (
    <a
      className="faq-file-block"
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      style={fileBlockStyle}
      onClick={handleClick}
    >
      <span className="faq-file-block__name" style={fileBlockNameStyle}>
        {getFileLabel(file)}
      </span>
    </a>
  )
}

const fileBlockStyle: CSSProperties = {
  display: 'block',
  margin: '12px 0',
  color: 'inherit',
  textDecoration: 'none',
  whiteSpace: 'normal',
}

const fileBlockNameStyle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  color: '#12b886',
  fontWeight: 700,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

function getFileLabel(file: FileBlockItem) {
  return file.name || file.url
}
